import React, {
    ChangeEvent,
    FormEvent,
    SyntheticEvent,
    RefObject,
    useState,
    useEffect,
    memo,
    useCallback,
    KeyboardEvent,
} from 'react';
import { Button, Spinner, Row, Col, Form, ToggleButtonGroup, ToggleButton } from 'react-bootstrap';

import useMatchStatFormShortCuts from '../../../helpers/useMatchStatFormShortCuts';
import { RosterEntry } from '../../../models/interfaces/RosterEntry';
import { MatchAdmin, MatchEventRecordingGroup, MatchEventRecordingOption } from '../../../models/interfaces/Match';
import { PerformerTypes } from '../../../models/interfaces/Stat';
import { MatchStatFormValues, DisplayTextObj } from '../../interfaces/MatchUI';
import { FormikTouched, FormikErrors } from 'formik';
import CustomToolTip from '../../../components/containers/CustomToolTip';
interface Props {
    matchAdmin: MatchAdmin;
    formikProps: {
        handleSubmit: (e?: FormEvent<HTMLFormElement> | undefined) => void;
        handleChange: {
            (e: ChangeEvent<any>): void;
            <T = string | ChangeEvent<any>>(field: T): T extends React.ChangeEvent<any>
                ? void
                : (e: string | ChangeEvent<any>) => void;
        };
        values: MatchStatFormValues;
        touched: FormikTouched<MatchStatFormValues>;
        errors: FormikErrors<MatchStatFormValues>;
        setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
        handleReset: (e?: SyntheticEvent<any, Event> | undefined) => void;
        isSubmitting: boolean;
    };
    displayTextObj: DisplayTextObj;
    formEl: RefObject<HTMLFormElement>;
    totalSeconds: number;
    handleClearForm: ({
        handleReset,
    }: {
        handleReset: (e?: SyntheticEvent<any, Event> | undefined) => void;
    }) => void;
    teamLists: {
        darkCapPlayerList: RosterEntry[] | undefined;
        lightCapPlayerList: RosterEntry[] | undefined;
    };
    showShortCutKeys: boolean;
    setShowShortCutKeys: (boolean) => void;
    isSelectedTab: boolean
}

const ModalStatFormDisplay = memo(
    ({
        matchAdmin,
        formikProps,
        displayTextObj,
        formEl,
        totalSeconds,
        handleClearForm,
        teamLists,
        showShortCutKeys,
        setShowShortCutKeys,
        isSelectedTab
    }: Props) => {
        const { match } = matchAdmin;
        const {
            handleSubmit,
            handleChange,
            values,
            touched,
            errors,
            setFieldValue,
            handleReset,
            isSubmitting,
        } = formikProps;

        const { handleKeyDown } = useMatchStatFormShortCuts({
            setFieldValue,
            matchAdmin,
            formEl,
            handleReset,
        });

        const addShortCutListener = useCallback(() => {
            window.addEventListener('keydown', handleKeyDown);
        }, [handleKeyDown]);

        const removeShortCutListener = useCallback(() => {
            window.removeEventListener('keydown', handleKeyDown);
        }, [handleKeyDown]);

        function handlePreventTab<
            T extends HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement
        >(e: KeyboardEvent, nextId: string) {
            if (e.key === 'Tab') {
                e.preventDefault();
                let clear = formEl?.current?.elements.namedItem(nextId) as T;
                clear && clear.focus();
            }
        }

        useEffect(() => {
            if (isSelectedTab) {
                addShortCutListener();
            }
            return () => removeShortCutListener();
        }, [addShortCutListener, removeShortCutListener, isSelectedTab]);

        const { darkCapPlayerList, lightCapPlayerList } = teamLists;

        const [statGroupsToShow, setStatGroupsToShow] = useState(matchAdmin.recordingOptionGroups)
        const recordingOptions = statGroupsToShow.reduce((currentOptions, recordingGroup) => {
            return currentOptions.concat(recordingGroup.recordingOptions)
        }, new Array<MatchEventRecordingOption>())

        return (
            <Form
                noValidate
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                }}
                ref={formEl}
            >
                <Row className="my-2">
                    <Col className="text-center">
                        <strong>{displayTextObj.titleText}</strong>
                    </Col>
                </Row>
                <Row className="my-2 justify-content-center">
                    <Col>
                        <Row className="justify-content-center py-1">
                            <ToggleButtonGroup 
                                name='recordingOptionPicker' 
                                type='checkbox' 
                                defaultValue={(matchAdmin.recordingOptionGroups.map((value, index) => {
                                    return index
                                }))}
                                onChange={(selectedIndicies) => {
                                    const selectedStatGroups = selectedIndicies.reduce((currentStatGroups, selectedIndex) => {
                                        if (selectedIndex < matchAdmin.recordingOptionGroups.length) {
                                            return currentStatGroups.concat([matchAdmin.recordingOptionGroups[selectedIndex]])
                                        } else {
                                            return currentStatGroups
                                        }
                                    }, new Array<MatchEventRecordingGroup>())
                                    setStatGroupsToShow(selectedStatGroups)
                                    setFieldValue('selectedStat', '')
                            }}>
                                {
                                    matchAdmin.recordingOptionGroups.map((optionGroup, index) => {
                                        return <ToggleButton variant="outline-info" value={index}>{optionGroup.displayName}</ToggleButton>
                                    }) 
                                }
                            </ToggleButtonGroup>
                        </Row>
                    <Form.Row className="text=center">
                        {recordingOptions.map((statObj, idx) => {
                                return (
                                    <Col
                                        key={statObj.displayName}
                                        xs={6}
                                        sm={4}
                                        className="py-1 px-md-2"
                                    >
                                        <CustomToolTip
                                            placement={idx % 3 === 0 ? 'left' : 'right'}
                                            displayText={`Shift + ${
                                                statObj.shortcutKey === '#'
                                                    ? '3'
                                                    : statObj.shortcutKey
                                            }`}
                                            id={statObj.displayName}
                                            show={showShortCutKeys}
                                        >
                                            <Button
                                                variant={
                                                    values.selectedStat === statObj
                                                        ? 'primary'
                                                        : 'outline-primary'
                                                }
                                                onClick={() => {
                                                    // set selectedStat to button selected
                                                    setFieldValue('selectedStat', statObj);
                                                    // if stat supportedPerformer is a player
                                                    if (
                                                        statObj.typeDescription.supportedPerfomers.has(
                                                            PerformerTypes.player
                                                        )
                                                    ) {
                                                        if (
                                                            statObj.typeDescription.supportedPerfomers.has(
                                                                PerformerTypes.team
                                                            )
                                                        ) {
                                                            // if stat performer can be a player or team (e.g. red/yellow cards first select the team value)
                                                            setFieldValue('selectedPlayer', '');
                                                        } else if (
                                                            // if stat performer is player check if selectedPlayer was a "non-player" option. if so remove value from field otherwise leave player value
                                                            values.selectedPlayer === 'notRequired'
                                                        ) {
                                                            setFieldValue('selectedPlayer', '');
                                                        }
                                                    } else {
                                                        //for non player stat tell user this field is not required. really we are selecting a value so that validation passes
                                                        setFieldValue(
                                                            'selectedPlayer',
                                                            'notRequired'
                                                        );
                                                    }
                                                    // if statement added for gameRemarks where a team isn't required
                                                    if (
                                                        statObj.requiresRemark &&
                                                        values.selectedTeam === ''
                                                    ) {
                                                        // empty object allows for form validation to still occur, as we are specifically making the value en empty object
                                                        setFieldValue('selectedTeam', {});
                                                    } else if (
                                                        // this else if statement will only overwrite an empty object which can only be provided by the above if. If a team is selected it will not be overwritten
                                                        Object.keys(values.selectedTeam).length ===
                                                        0
                                                    ) {
                                                        setFieldValue('selectedTeam', '');
                                                    }
                                                }}
                                                className="shadow-sm btn-block"
                                            >
                                                {statObj.displayName}
                                            </Button>
                                        </CustomToolTip>
                                    </Col>
                                );
                            })}
                            </Form.Row>
                        {/* if selectedStat error mock a form field error */}
                        {errors.selectedStat && touched.selectedStat && (
                            <Row>
                                <Col xs={12}>
                                    <div className="invalid-feedback" style={{ display: 'block' }}>
                                        {errors.selectedStat}
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </Col>
                </Row>
                <Row className="my-2">
                    <Col>
                        <Row className="my-2">
                            <Col className="text-center">
                                <strong>Light Caps</strong>
                            </Col>
                            <Col className="text-center">
                                <strong>Dark Caps</strong>
                            </Col>
                        </Row>
                        <Row className="my-2">
                            <Col className="text-center">
                                <CustomToolTip
                                    placement="top"
                                    displayText="l"
                                    id="lightCapTeam"
                                    show={showShortCutKeys}
                                >
                                    <Button
                                        variant={
                                            values.selectedTeam === match.lightCapTeam
                                                ? 'primary'
                                                : 'outline-primary'
                                        }
                                        onClick={() => {
                                            setFieldValue('selectedTeam', match.lightCapTeam);
                                            if (
                                                // checks that stat requires a player & if so resets the player to empty NOTE need to add any changes to darkTeam as well
                                                values.selectedStat === '' ||
                                                values.selectedStat.typeDescription.supportedPerfomers.has(
                                                    PerformerTypes.player
                                                )
                                            ) {
                                                setFieldValue('selectedPlayer', '');
                                            }
                                        }}
                                        className="shadow-sm"
                                        id="lightCapTeam"
                                    >
                                        {match.lightCapTeam.name}
                                    </Button>
                                </CustomToolTip>
                            </Col>
                            <Col className="text-center">
                                <CustomToolTip
                                    placement="top"
                                    displayText="d"
                                    id="darkCapTeam"
                                    show={showShortCutKeys}
                                >
                                    <Button
                                        variant={
                                            values.selectedTeam === match.darkCapTeam
                                                ? 'primary'
                                                : 'outline-primary'
                                        }
                                        onClick={() => {
                                            setFieldValue('selectedTeam', match.darkCapTeam);
                                            if (
                                                // checks that stat requires a player & if so resets the player to empty NOTE need to add any changes to lightTeam as well
                                                values.selectedStat === '' ||
                                                values.selectedStat.typeDescription.supportedPerfomers.has(
                                                    PerformerTypes.player
                                                )
                                            ) {
                                                setFieldValue('selectedPlayer', '');
                                            }
                                        }}
                                        className="shadow-sm"
                                        id="darkCapTeam"
                                    >
                                        {match.darkCapTeam.name}
                                    </Button>
                                </CustomToolTip>
                            </Col>
                        </Row>
                        {/* if selectedTeam error mock a form field error */}
                        {errors.selectedTeam && touched.selectedTeam && (
                            <Row className="justify-content-center">
                                <Col xs="auto">
                                    <div className="invalid-feedback" style={{ display: 'block' }}>
                                        {errors.selectedTeam}
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Row>
                            <Col className="my-2 text-center">
                                <strong>Select Player</strong>
                            </Col>
                            <Col className="my-2 text-center">
                                <strong>Select Time</strong>
                            </Col>
                        </Row>
                        <Form.Row className="my-2">
                            <Form.Group controlId="selectedPlayer" as={Col} sm="auto">
                                <CustomToolTip
                                    placement="bottom"
                                    displayText="p"
                                    id="player"
                                    show={showShortCutKeys}
                                >
                                    <Form.Control
                                        as="select"
                                        custom
                                        name="selectedPlayer"
                                        value={values.selectedPlayer}
                                        onChange={handleChange}
                                        isValid={touched.selectedPlayer && !errors.selectedPlayer}
                                        isInvalid={
                                            touched.selectedPlayer && !!errors.selectedPlayer
                                        }
                                        data-testid="selectedPlayer"
                                    >
                                        <option value="" disabled hidden>
                                            {!values.selectedTeam || !('id' in values.selectedTeam)
                                                ? 'Select A Team First'
                                                : 'Select Player'}
                                        </option>
                                        {/*handles stats where the supported performer is a player and provide a dropdown list of players in that team (e.g. goal) */}
                                        {!values.selectedTeam ||
                                        (values.selectedStat &&
                                            !values.selectedStat.typeDescription.supportedPerfomers.has(
                                                PerformerTypes.player
                                            ))
                                            ? null
                                            : darkCapPlayerList &&
                                              values.selectedTeam === match.darkCapTeam
                                            ? darkCapPlayerList.map((player) => (
                                                  <option key={player.id} value={player.id}>
                                                      {`${player.capNumber.displayName}. ${player.name.firstName} ${player.name.lastName}`}
                                                  </option>
                                              ))
                                            : lightCapPlayerList &&
                                              values.selectedTeam === match.lightCapTeam &&
                                              lightCapPlayerList.map((player) => (
                                                  <option key={player.id} value={player.id}>
                                                      {`${player.capNumber.displayName}. ${player.name.firstName} ${player.name.lastName}`}
                                                  </option>
                                              ))}
                                        {/* handles stats where the supported performer is does not include a player (e.g. timeout)*/}
                                        {values.selectedStat &&
                                            !values.selectedStat.typeDescription.supportedPerfomers.has(
                                                PerformerTypes.player
                                            ) && (
                                                <option value="notRequired">
                                                    Player Not Required
                                                </option>
                                            )}
                                        {/* handles stats where the supported performer is a player or a team (e.g. red/yellow card) */}
                                        {values.selectedStat &&
                                            values.selectedStat.typeDescription.supportedPerfomers.has(
                                                PerformerTypes.player
                                            ) &&
                                            values.selectedStat.typeDescription.supportedPerfomers.has(
                                                PerformerTypes.team
                                            ) &&
                                            !(
                                                values.selectedTeam === '' ||
                                                !('id' in values.selectedTeam)
                                            ) && <option value="team">Team</option>}
                                    </Form.Control>
                                </CustomToolTip>
                                <Form.Control.Feedback type="invalid">
                                    {errors.selectedPlayer}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group
                                controlId="selectedPeriod"
                                as={Col}
                                sm={2}
                                className="ml-auto"
                            >
                                <CustomToolTip
                                    placement="bottom"
                                    displayText="q"
                                    id="period"
                                    show={showShortCutKeys}
                                >
                                    <Form.Control
                                        as="select"
                                        custom
                                        name="selectedPeriod"
                                        value={values.selectedPeriod}
                                        onChange={handleChange}
                                        isValid={touched.selectedPeriod && !errors.selectedPeriod}
                                        isInvalid={
                                            touched.selectedPeriod && !!errors.selectedPeriod
                                        }
                                    >
                                        {match.parameters.regularTimeDescription.matchSegments
                                            .concat(
                                                match.parameters.overtimeDescription.matchSegments
                                            )
                                            .map((period) => (
                                                <option
                                                    key={period.databaseValue}
                                                    value={period.databaseValue}
                                                >
                                                    {period.displayName}
                                                </option>
                                            ))}
                                    </Form.Control>
                                </CustomToolTip>
                                <Form.Control.Feedback type="invalid">
                                    {errors.selectedPeriod}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <CustomToolTip
                                placement={showShortCutKeys ? 'bottom' : 'top'}
                                displayText={
                                    showShortCutKeys
                                        ? 'm'
                                        : `Minutes to be between 0-${totalSeconds / 60}`
                                }
                                id="minutes"
                                show={showShortCutKeys ? showShortCutKeys : undefined}
                            >
                                <Form.Group
                                    data-testid="selectedMinutes"
                                    controlId="selectedMinutes"
                                    as={Col}
                                    sm={2}
                                >
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        max={totalSeconds / 60}
                                        name="selectedMinutes"
                                        value={values.selectedMinutes}
                                        onKeyPress={(e: KeyboardEvent) => {
                                            if (!/[0-9]/.test(e.key) && e.key !== 'Enter') {
                                                // if non numeric value was passed don't proceed
                                                e.preventDefault();
                                            }
                                        }}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            setFieldValue(
                                                'selectedMinutes',
                                                e.currentTarget.value === ''
                                                    ? ''
                                                    : e.currentTarget.value
                                            );
                                            // if the biggest minute value is single digit focus on secs. Also checks that a digit was provided
                                            if (
                                                totalSeconds / 60 < 10 &&
                                                !(e.currentTarget.value === '') &&
                                                /[0-9]/.test(e.currentTarget.value)
                                            ) {
                                                let selectedSec =
                                                    formEl?.current?.elements.namedItem(
                                                        'selectedSeconds'
                                                    ) as HTMLInputElement;
                                                selectedSec && selectedSec.focus();
                                                // if biggest minute value is two digit focus on sec once two digits have been entered
                                            } else if (
                                                e.currentTarget.value.length >=
                                                Math.floor(totalSeconds / 60)
                                            ) {
                                                let selectedSec =
                                                    formEl?.current?.elements.namedItem(
                                                        'selectedSeconds'
                                                    ) as HTMLInputElement;
                                                selectedSec && selectedSec.focus();
                                            }
                                        }}
                                        isValid={touched.selectedMinutes && !errors.selectedMinutes}
                                        isInvalid={
                                            touched.selectedMinutes && !!errors.selectedMinutes
                                        }
                                        placeholder="min"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.selectedMinutes}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </CustomToolTip>
                            <CustomToolTip
                                placement={showShortCutKeys ? 'bottom' : 'top'}
                                displayText={showShortCutKeys ? 's' : ''}
                                id="seconds"
                                show={showShortCutKeys ? showShortCutKeys : undefined}
                            >
                                <Form.Group
                                    controlId="selectedSeconds"
                                    data-testid="selectedSeconds"
                                    as={Col}
                                    sm={2}
                                >
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        max="60"
                                        name="selectedSeconds"
                                        value={values.selectedSeconds}
                                        onKeyPress={(e: KeyboardEvent) => {
                                            if (!/[0-9]/.test(e.key) && e.key !== 'Enter') {
                                                // if non numeric value was passed don't proceed
                                                e.preventDefault();
                                            }
                                        }}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            setFieldValue(
                                                'selectedSeconds',
                                                e.currentTarget.value === ''
                                                    ? ''
                                                    : e.currentTarget.value
                                            );
                                        }}
                                        isValid={touched.selectedSeconds && !errors.selectedSeconds}
                                        isInvalid={
                                            touched.selectedSeconds && !!errors.selectedSeconds
                                        }
                                        placeholder="sec"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.selectedSeconds}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </CustomToolTip>
                        </Form.Row>
                    </Col>
                </Row>
                <Row>
                    <Form.Group controlId="selectedRemark" data-testid="selectedRemark" as={Col}>
                        <Form.Label className="my-2">
                            <strong>Notes</strong>
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            name="selectedRemark"
                            value={values.selectedRemark}
                            onChange={handleChange}
                            isValid={touched.selectedRemark && !errors.selectedRemark}
                            isInvalid={touched.selectedRemark && !!errors.selectedRemark}
                            placeholder="Stat Remarks..."
                            onFocus={removeShortCutListener}
                            onBlur={addShortCutListener}
                            onKeyDown={(e: KeyboardEvent) =>
                                handlePreventTab<HTMLButtonElement>(e, 'clear')
                            }
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.selectedRemark}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Row>
                <Form.Row className="pt-2 justify-content-between">
                    <Button
                                variant="info"
                                onClick={() => setShowShortCutKeys((st) => !st)}
                                // get rid of underline & shadow on focus event
                                style={{ textDecoration: 'none', boxShadow: 'none' }}
                            >
                                {showShortCutKeys ? 'Hide hot keys' : 'Show hot keys'}
                            </Button>
                    <Col>
                        <Row className="justify-content-end">
                            <CustomToolTip
                                placement="bottom"
                                displayText="c"
                                id="clear"
                                show={showShortCutKeys}
                            >
                                <Button
                                    variant="outline-primary"
                                    onClick={() => handleClearForm({ handleReset })}
                                    className="shadow-sm"
                                    id="clear"
                                    onKeyDown={(e: KeyboardEvent) =>
                                        handlePreventTab<HTMLButtonElement>(e, 'submit')
                                    }
                                >
                                    Clear
                                </Button>
                            </CustomToolTip>
                            <CustomToolTip
                                placement="bottom"
                                displayText="enter"
                                id="submit"
                                show={showShortCutKeys}
                            >
                                <Button type="submit" className="shadow-sm mx-2" id="submit">
                                    {isSubmitting ? (
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                    ) : (
                                        displayTextObj.buttonText
                                    )}
                                </Button>
                            </CustomToolTip>
                        </Row>
                    </Col>
                </Form.Row>
            </Form>
        );
    }
);

export default ModalStatFormDisplay;
